// Wrapper that fetches the Overview payload
// once and mounts the three panels. Mounted from <DealOverviewTab>.
//
// On a Declined or Withdrawn deal (`stoppedDeal`) the panels show only when the
// server says the deal reached Underwriting (owner ruling, 2026-10-26: the data
// feeds the deal-flow metrics and Failure Tracking, so it stays visible). The
// gates then carry no links: the deal page does not open the Underwriting,
// analyst or Closing tabs on a stopped deal.

import { CertificationGatesPanel } from "./CertificationGatesPanel";
import { SlaHealthPanel } from "./SlaHealthPanel";
import { FinalOutcomePanel } from "./FinalOutcomePanel";
import { useDealFlowOverview } from "@/hooks/dealFlow/useDealFlow";

interface Props {
  dealId: string;
  /** A Declined or Withdrawn deal: render nothing unless it reached Underwriting. */
  stoppedDeal?: boolean;
}

export function DealFlowOverviewPanels({ dealId, stoppedDeal = false }: Props) {
  const { data, isLoading, isError } = useDealFlowOverview(dealId);

  if (isLoading) {
    // A stopped deal may never have reached Underwriting; say nothing until
    // the server answers.
    if (stoppedDeal) return null;
    return (
      <div role="status" aria-live="polite" className="text-gray-mid">
        Loading deal flow overview…
      </div>
    );
  }
  if (isError || !data) {
    return (
      <div role="alert" className="text-status-error">
        Failed to load deal flow overview.
      </div>
    );
  }
  if (stoppedDeal && !data.reachedUnderwriting) return null;
  return (
    <div className="grid grid-cols-1 gap-space-3">
      <CertificationGatesPanel
        dealId={dealId}
        tabs={data.certificationGates.tabs}
        linkToTabs={!stoppedDeal}
      />
      <SlaHealthPanel tabs={data.slaHealth.tabs} />
      <FinalOutcomePanel outcome={data.finalOutcome} />
    </div>
  );
}
